import { useEffect, useRef, useState } from 'react'
import type { ChangeEvent, MouseEvent } from 'react'
import { TabAccordion } from '~/components-v2/tab-accordion/tab-accordion'
import type { TabAccordionItem } from '~/components-v2/tab-accordion/tab-accordion'
import { BUILDS, TAG_LINKS } from './data/builds'
import type { Scene } from './data/builds'
import agentLoopScene from './scenes/agent-loop.svg?raw'
import bulkUsersScene from './scenes/bulk-users.svg?raw'
import ciCdScene from './scenes/ci-cd.svg?raw'
import dataProductsScene from './scenes/data-products.svg?raw'
import embeddedScene from './scenes/embedded.svg?raw'
import handshakeLogo from './logos/handshake.svg?raw'
import bamboohrLogo from './logos/bamboohr.svg?raw'
import criblLogo from './logos/cribl.svg?raw'

const NAME = 'developers-builds'
const LOOP_MS = 24000
const STILL_MS = 8000
const SCENES: Record<string, string> = {
  'ci-cd': ciCdScene,
  'agent-loop': agentLoopScene,
  'bulk-users': bulkUsersScene,
  'data-products': dataProductsScene,
  embedded: embeddedScene,
}

/** A loop holds for its length and a video until it ends; a still or a placeholder moves on sooner. */
const holdFor = (scene?: Scene) =>
  scene?.kind === 'svg' ? LOOP_MS : scene?.kind === 'video' ? null : STILL_MS

/** Clicks the next build's radio unless the reader has picked one; the click is flagged so onChange does not
 *  count it as a touch. */
const advanceFrom = (
  activeId: string,
  touched: { current: boolean },
  programmatic: { current: boolean },
) => {
  if (touched.current) return
  const current = BUILDS.findIndex((b) => b.id === activeId)
  const next = BUILDS[(current + 1) % BUILDS.length]
  const input = document.getElementById(
    `${NAME}-${next.id}`,
  ) as HTMLInputElement | null
  if (!input) return
  programmatic.current = true
  input.click()
}

const renderScene = (id: string, label: string, scene?: Scene) => {
  if (scene?.kind === 'svg')
    return (
      <div
        className="stage-scene"
        dangerouslySetInnerHTML={{ __html: SCENES[scene.name] }}
      />
    )
  if (scene?.kind === 'video')
    return (
      <video
        className="stage-video"
        data-build={id}
        muted
        playsInline
        preload="metadata"
        poster={scene.poster}
        src={scene.src}
      />
    )
  if (scene?.kind === 'image')
    return <img alt="" className="stage-image" src={scene.src} />
  return (
    <div className="stage-placeholder">
      <b>looping video</b>
      {label}
    </div>
  )
}
const LOGOS: Record<string, string> = {
  handshake: handshakeLogo,
  bamboohr: bamboohrLogo,
  cribl: criblLogo,
}

const items: TabAccordionItem[] = BUILDS.map((build) => ({
  id: build.id,
  label: build.label,
  description: (
    <>
      {build.line}
      <span className="build-tags">
        {build.tags.map((tag) => (
          <a
            className={`t-link k-${TAG_LINKS[tag].startsWith('https://github.com/') ? 'github' : 'docs'}`}
            href={TAG_LINKS[tag]}
            key={tag}
            rel="noopener noreferrer"
            target="_blank"
          >
            {tag}
          </a>
        ))}
      </span>
    </>
  ),
  screenshotAspectRatio: '1200 / 750',
  screenshotLabel: build.label,
  screenshotContent: renderScene(build.id, build.label, build.scene),
}))

/** Five recipes on the site's tab accordion, with a proof card under the stage for the active build.
 *  Until the reader picks one, the section cycles: a loop holds for its length, a video plays through, a still
 *  or a placeholder advances sooner. */
export const WhatPeopleBuild = () => {
  const [activeId, setActiveId] = useState(BUILDS[0].id)
  const touched = useRef(false)
  const programmatic = useRef(false)

  const onChange = (event: ChangeEvent<HTMLDivElement>) => {
    const input = event.target as HTMLInputElement
    if (input.name !== NAME) return
    if (!programmatic.current) touched.current = true
    programmatic.current = false
    setActiveId(input.value)
  }

  useEffect(() => {
    if (touched.current) return
    const wait = holdFor(BUILDS.find((b) => b.id === activeId)?.scene)
    if (wait === null) return
    const timer = setTimeout(
      () => advanceFrom(activeId, touched, programmatic),
      wait,
    )
    return () => clearTimeout(timer)
  }, [activeId])

  /** The menu is pinned to its open height (every one-liner reserves the same lines, so each build's open state
   *  is the same height) and clips its content, so the row and the band hold still while panels animate; the
   *  stage stretches to that height. Re-measured on resize; released on narrow screens. */
  useEffect(() => {
    const tabs = document.querySelector<HTMLElement>(
      '.developers-builds .tab-accordion-tabs',
    )
    if (!tabs) return
    const section = tabs.closest<HTMLElement>('.developers-builds')
    const pin = () => {
      tabs.style.height = ''
      section?.style.removeProperty('--builds-menu-height')
      if (window.innerWidth < 1024) return
      const height = `${Math.ceil(tabs.getBoundingClientRect().height)}px`
      tabs.style.height = height
      section?.style.setProperty('--builds-menu-height', height)
    }
    pin()
    let timer: ReturnType<typeof setTimeout>
    const onResize = () => {
      clearTimeout(timer)
      timer = setTimeout(pin, 150)
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      clearTimeout(timer)
    }
  }, [])

  /** Every panel mounts at load, so a loop would be mid-way when its build opens; restart it from the prompt.
   *  Only the active build's video plays; the others sit paused behind it. When a video ends the section moves
   *  on if the reader has not picked a build yet, and replays otherwise. */
  useEffect(() => {
    document
      .querySelector(
        `.developers-builds .tab-accordion-screenshot[data-id="${activeId}"] .stage-scene svg`,
      )
      ?.getAnimations({ subtree: true })
      .forEach((animation) => {
        animation.currentTime = 0
      })
    const videos = document.querySelectorAll<HTMLVideoElement>(
      `.developers-builds .stage-video`,
    )
    videos.forEach((video) => {
      if (video.dataset.build === activeId) {
        video.currentTime = 0
        video.play().catch(() => {})
      } else video.pause()
    })
    const active = Array.from(videos).find((v) => v.dataset.build === activeId)
    if (!active) return
    const onEnded = () => {
      if (touched.current) {
        active.currentTime = 0
        active.play().catch(() => {})
      } else advanceFrom(activeId, touched, programmatic)
    }
    active.addEventListener('ended', onEnded)
    return () => active.removeEventListener('ended', onEnded)
  }, [activeId])

  /** A real click on a label focuses its radio, and the accordion's radios sit hidden at the top of the section,
   *  so the browser scrolls the page up to them. Checking the radio without focusing it keeps the page still.
   *  Any click on a label counts as the reader taking over, the open build's included, so the cycling stops. */
  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const label = (event.target as HTMLElement).closest('label')
    if (!label?.htmlFor.startsWith(`${NAME}-`)) return
    const input = document.getElementById(
      label.htmlFor,
    ) as HTMLInputElement | null
    if (!input) return
    touched.current = true
    if (input.checked) return
    event.preventDefault()
    input.click()
  }

  const active = BUILDS.find((b) => b.id === activeId) ?? BUILDS[0]
  const { proof } = active

  return (
    <div
      className="page-section developers-builds bg-brand-dark"
      id="build"
      onChange={onChange}
      onClick={onClick}
    >
      <TabAccordion heading="Common workflows" items={items} name={NAME} />
      <div className="page-section__inner">
        <aside className={proof.logo ? 'proof' : 'proof proof--nologo'}>
          {proof.logo ? (
            <span
              className="proof__logo"
              dangerouslySetInnerHTML={{ __html: LOGOS[proof.logo] }}
            />
          ) : null}
          <div className="proof__body">
            <div className="proof__kind caps-xs">{proof.title}</div>
            <p className="proof__blurb body-s">{proof.blurb}</p>
          </div>
          <a className="proof__cta" href={proof.href}>
            {proof.cta}
          </a>
        </aside>
      </div>
    </div>
  )
}
